import { Middleware, isRejectedWithValue } from '@reduxjs/toolkit';
import logger from '../../utils/logger';

/**
 * Middleware for handling errors in Redux actions
 * This middleware listens for rejected async thunks and handles them accordingly
 * IMPORTANT: This middleware ONLY logs errors - it NEVER triggers logout or auth state changes
 */
export const errorMiddleware: Middleware = (store) => (next) => (action) => {
  // Check if action is a rejected async thunk with value
  if (isRejectedWithValue(action)) {
    const errorMessage = action.payload || 'An unknown error occurred';
    
    // Skip error handling for expected/common errors
    const skipErrorsFor = [
      'auth/logoutAsync/rejected',
      'training/fetchTrainingById/rejected', // Expected when visiting /training/add
      'notifications/fetchUnreadCount/rejected', // Expected if notification permissions are missing
    ];
    
    // Actions that should log as warnings instead of errors
    const warnOnlyFor = [
      'notifications/fetchNotifications/rejected',
      'notifications/fetchUnreadCount/rejected',
    ];

    // Only log if not in skip list
    if (!skipErrorsFor.includes(action.type)) {
      if (warnOnlyFor.includes(action.type)) {
        logger.warn('Redux action warning:', {
          type: action.type,
          error: errorMessage,
        });
      } else {
        logger.error('Redux action error:', {
          type: action.type,
          error: errorMessage,
          meta: action.meta,
        });
      }
    }

    // Log to error reporting service if available (but not for expected errors)
    if (process.env.NODE_ENV === 'production' && !skipErrorsFor.includes(action.type)) {
      const { reportError } = require('../../utils/error-reporting');
      reportError({
        message: errorMessage,
        source: action.type,
        additionalInfo: {
          actionType: action.type,
          actionMeta: action.meta,
          timestamp: new Date().toISOString(),
        }
      });
    }
  }

  return next(action);
};

export default errorMiddleware;